import CustomerAverageAgeBars from '@/components/Customers/CustomreIndicators/CustomerAverageAgeBars';
import CustomerQuantityPercentagePie from '@/components/Customers/CustomreIndicators/CustomerQuantityPercentagePie';
import CustomerStaticsIndicators from '@/components/Customers/CustomreIndicators/CustomerStatisticsIndicators';
import {
  DefaultRouteTabs,
  FloatingFormItem,
  InputDate,
} from '@/components/_common';
import {
  useCustomersIndicators,
  useCustomersIndicatorsGranularity,
} from '@/core/domain/people/Customers/Customers.hooks';
import {
  CustomerIndicatorForm,
  CustomerRegisterInterface,
  GranularityType,
} from '@/core/domain/people/Customers/Customers.types';
import { PageProps } from '@/types/PageProps';
import { Alert, Col, Divider, Form, Row, Select } from 'antd';
import moment, { Moment } from 'moment';
import { useDebounce } from '@/hooks';
import { useEffect } from 'react';
import CustomerStatisticIndicatorsChart from '@/components/Customers/CustomreIndicators/CustomerStatisticIndicatorsChart';

export default function CustomerIndicatorPage({ route }: PageProps) {
  const [form] = Form.useForm<CustomerIndicatorForm>();

  const formValues = Form.useWatch([], form);
  const granularity = formValues?.granularity;
  const initialDate = formValues?.initialDate;

  useEffect(() => {
    if (granularity === GranularityType.DAY) {
      form.setFieldsValue({
        initialDate: moment().subtract(30, 'days'),
        endDate: moment(),
      });
    }

    if (granularity === GranularityType.WEEK) {
      form.setFieldsValue({
        initialDate: moment().subtract(90, 'days'),
        endDate: moment(),
      });
    }

    if (granularity === GranularityType.MONTH) {
      form.setFieldsValue({
        initialDate: moment().subtract(12, 'months'),
        endDate: moment(),
      });
    }

    if (granularity === GranularityType.YEAR) {
      form.setFieldsValue({
        initialDate: moment().subtract(5, 'years'),
        endDate: moment(),
      });
    }
  }, [granularity]);

  const queryDebounce = useDebounce(formValues, 500);

  const customersIndicators = useCustomersIndicators();

  const customersIndicatorsGranularity = useCustomersIndicatorsGranularity({
    ...queryDebounce,
    initialDate: queryDebounce?.initialDate?.format('YYYY-MM-DD'),
    endDate: queryDebounce?.endDate?.format('YYYY-MM-DD'),
    order: 'ASC',
  });

  const baseColor = ['#339AF0', '#F06595', '#ADB5BD', '#51CF66'];

  const filterRoutes = route.parent.routes.filter(
    ({ key }) => key === 'indicators' || key === 'list',
  );

  const granularityOptions = [
    {
      label: 'Dia: Máximo de 30 dias',
      value: GranularityType.DAY,
    },
    {
      label: 'Semana: Máximo de 90 dias',
      value: GranularityType.WEEK,
    },
    {
      label: 'Mês: Máximo de 12 meses',
      value: GranularityType.MONTH,
    },
    {
      label: 'Ano: Máximo de 5 anos',
      value: GranularityType.YEAR,
    },
  ];

  enum period {
    days = 'days',
    months = 'months',
    years = 'years',
  }

  const granularityDays = {
    [GranularityType.DAY]: { days: 30, type: period.days },
    [GranularityType.WEEK]: { days: 90, type: period.days },
    [GranularityType.MONTH]: { days: 12, type: period.months },
    [GranularityType.YEAR]: { days: 5, type: period.years },
  };

  const disabledDate = (current: Moment) => {
    if (!current) {
      return false;
    }

    if (granularity) {
      const { days, type } = granularityDays[granularity];

      const lastAllowedDay = initialDate?.clone().add(days, type);
      return current.isBefore(initialDate) || current.isAfter(lastAllowedDay);
    }
    return true;
  };

  const handleAgeGender = (data?: CustomerRegisterInterface[]) => {
    let averageAgeMaleCustomers = 0;
    let averageAgeFemaleCustomers = 0;
    let total = 0;
    let maleCustomersCount = 0;
    let femaleCustomersCount = 0;
    let averageAgeGenderUnspecifiedCustomers = 0;
    let quantatyPeriodMale = 0;
    let quantatyPeriodFemale = 0;
    let quantatyPeriodUnspecified = 0;

    data?.map((period) => {
      quantatyPeriodMale += period.averageMaleAge > 0 ? 1 : 0;
      quantatyPeriodFemale += period.averageFemaleAge > 0 ? 1 : 0;
      quantatyPeriodUnspecified += period.averageNoGenderAge > 0 ? 1 : 0;

      averageAgeMaleCustomers += period.averageMaleAge;
      averageAgeFemaleCustomers += period.averageFemaleAge;
      total += period.total;
      maleCustomersCount += period.male;
      femaleCustomersCount += period.female;
      averageAgeGenderUnspecifiedCustomers += period.averageNoGenderAge;
    });

    return {
      genderUnspecifiedCustomersCount:
        total - maleCustomersCount - femaleCustomersCount,
      maleCustomersCount,
      femaleCustomersCount,
      averageAgeMaleCustomers: averageAgeMaleCustomers / quantatyPeriodMale,
      averageAgeFemaleCustomers:
        averageAgeFemaleCustomers / quantatyPeriodFemale,
      averageAgeGenderUnspecifiedCustomers:
        averageAgeGenderUnspecifiedCustomers / quantatyPeriodUnspecified,
    };
  };

  const formattedData = handleAgeGender(customersIndicatorsGranularity.data);

  route.parent.routes = filterRoutes;

  return (
    <DefaultRouteTabs
      route={route.parent}
      basePath={route.parent.path}
      baseKey={route.parent.key}
    >
      <Form
        form={form}
        onValuesChange={(event) => {
          if (!('endDate' in event)) {
            form.setFieldValue('endDate', null);
          }
        }}
      >
        <Row gutter={[16, 16]}>
          <Col span={24} md={8}>
            <FloatingFormItem
              label="Período"
              name="granularity"
              initialValue={GranularityType.MONTH}
            >
              <Select options={granularityOptions} />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={8}>
            <FloatingFormItem
              label="De:"
              name="initialDate"
              initialValue={moment().subtract(12, 'months')}
            >
              <InputDate />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={8}>
            <FloatingFormItem
              label="Até:"
              name="endDate"
              initialValue={moment()}
            >
              <InputDate disabledDate={disabledDate} />
            </FloatingFormItem>
          </Col>
        </Row>

        <Divider />

        <Row gutter={[24, 24]}>
          <Col span={24}>
            <Alert
              description="Para a composição deste dashboard, foram considerados exclusivamente os clientes que possuem a data de aniversário cadastrada em nosso sistema."
              type="success"
            />
          </Col>

          <Col span={24}>
            <CustomerStaticsIndicators
              customersIndicators={customersIndicators.data}
              dateTreated={formattedData}
              baseColor={baseColor}
            />
          </Col>

          <Col span={24}>
            <CustomerStatisticIndicatorsChart
              granularity={granularity}
              baseColor={baseColor}
              customerRegister={customersIndicatorsGranularity.data}
            />
          </Col>

          <Col span={24} md={12}>
            <CustomerQuantityPercentagePie
              dateTreated={formattedData}
              baseColor={baseColor}
            />
          </Col>

          <Col span={24} md={12}>
            <CustomerAverageAgeBars
              dateTreated={formattedData}
              baseColor={baseColor}
            />
          </Col>
        </Row>
      </Form>
    </DefaultRouteTabs>
  );
}
